/**
 * Deterministic seeded shuffle using Mulberry32 PRNG + Fisher-Yates.
 *
 * Given the same seed string, the output order is always identical,
 * so each CBT session gets a unique but stable question order that
 * survives page refreshes.
 */
export const seededShuffle = <T,>(array: T[], seed: string): T[] => {
  const result = [...array];

  // Simple hash from string → number
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = ((hash << 5) - hash + seed.charCodeAt(i)) | 0;
  }

  // Mulberry32 PRNG seeded from hash
  let s = Math.abs(hash) | 0;
  const rand = () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  // Fisher-Yates shuffle
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }

  return result;
};
